export interface IndustryOption {
  slug: string;
  label: string;
  description: string;
}

export const DEFAULT_INDUSTRY_SLUG = 'interior-design';

export const CANONICAL_INDUSTRY_SLUGS = [
  'interior-design',
  'construction-civil',
  'landscaping',
  'retail-commercial',
  'information-technology',
  'events-hospitality',
  'healthcare-labs',
  'manufacturing-industrial',
  'consulting-professional-services',
  'automobile-services'
];

export const INDUSTRY_OPTIONS: IndustryOption[] = [
  {
    slug: 'interior-design',
    label: 'Interior Design',
    description: 'Residential and commercial fit-out estimates.',
  },
  {
    slug: 'construction-civil',
    label: 'Construction & Civil',
    description: 'Turnkey construction, renovation, and BOQ workflows.',
  },
  {
    slug: 'landscaping',
    label: 'Landscaping',
    description: 'Garden, irrigation, and hardscape estimates.',
  },
  {
    slug: 'retail-commercial',
    label: 'Retail & Commercial',
    description: 'Retail stores, restaurants, and office interiors.',
  },
  {
    slug: 'information-technology',
    label: 'Information Technology',
    description: 'Software, infrastructure, and AMC project estimates.',
  },
  {
    slug: 'events-hospitality',
    label: 'Events & Hospitality',
    description: 'Event production, venues, and hospitality scopes.',
  },
  {
    slug: 'healthcare-labs',
    label: 'Healthcare & Labs',
    description: 'Clinic, lab, and healthcare setup estimates.',
  },
  {
    slug: 'manufacturing-industrial',
    label: 'Manufacturing & Industrial',
    description: 'Plant setup, equipment, and maintenance scopes.',
  },
  {
    slug: 'consulting-professional-services',
    label: 'Consulting & Professional Services',
    description: 'Advisory, audit, and training proposals.',
  },
  {
    slug: 'automobile-services',
    label: 'Automobile Services',
    description: 'Service, repair, and fleet maintenance estimates.',
  },
];

export const INDUSTRY_SLUG_ALIASES: Record<string, string> = {
  // Short/legacy slugs
  interior: 'interior-design',
  construction: 'construction-civil',
  it: 'information-technology',
  retail: 'retail-commercial',
  events: 'events-hospitality',
  healthcare: 'healthcare-labs',
  manufacturing: 'manufacturing-industrial',
  consulting: 'consulting-professional-services',
  auto: 'automobile-services',

  // Other variations
  architecture: 'construction-civil',
  'civil-construction': 'construction-civil',
  civil_works: 'construction-civil',
  furniture: 'retail-commercial',
  'interior-design': 'interior-design',
  interior_design: 'interior-design',
  'it-consulting': 'information-technology',
  it_projects: 'information-technology',
  landscape: 'landscaping',
  photography: 'events-hospitality',
  retail_fitout: 'retail-commercial',
};

const INDUSTRY_OPTION_BY_SLUG = new Map(
  INDUSTRY_OPTIONS.map((option) => [option.slug, option]),
);

function sanitizeIndustrySlug(value: string): string {
  return value.trim().toLowerCase().replace(/[_\s]+/g, '-');
}

export function normalizeIndustrySlug(rawSlug?: string | null): string {
  if (!rawSlug) return DEFAULT_INDUSTRY_SLUG;

  const cleanedSlug = sanitizeIndustrySlug(rawSlug);
  const canonicalSlug =
    INDUSTRY_SLUG_ALIASES[cleanedSlug] ??
    cleanedSlug;

  if (INDUSTRY_OPTION_BY_SLUG.has(canonicalSlug)) {
    return canonicalSlug;
  }

  return DEFAULT_INDUSTRY_SLUG;
}

export function normalizeIndustrySlugs(rawSlugs: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(
      rawSlugs
        .filter((slug): slug is string => typeof slug === 'string' && slug.trim().length > 0)
        .map((slug) => normalizeIndustrySlug(slug)),
    ),
  );
}

export function getIndustryOption(rawSlug?: string | null): IndustryOption {
  const canonicalSlug = normalizeIndustrySlug(rawSlug);
  return INDUSTRY_OPTION_BY_SLUG.get(canonicalSlug) ?? INDUSTRY_OPTIONS[0];
}

export function getScopeStepCopy(rawSlug?: string | null): {
  label: string;
  description: string;
} {
  switch (normalizeIndustrySlug(rawSlug)) {
    case 'construction-civil':
    case 'manufacturing-industrial':
      return {
        label: 'Works',
        description: 'Select work packages and quantities for this project',
      };
    case 'information-technology':
    case 'consulting-professional-services':
      return {
        label: 'Modules',
        description: 'Select the modules or workstreams for this project',
      };
    case 'events-hospitality':
    case 'retail-commercial':
    case 'landscaping':
      return {
        label: 'Zones',
        description: 'Select the zones or areas included in this project',
      };
    case 'automobile-services':
      return {
        label: 'Systems',
        description: 'Select the service systems covered in this estimate',
      };
    case 'healthcare-labs':
      return {
        label: 'Areas',
        description: 'Select the clinical areas covered in this project',
      };
    case 'interior-design':
    default:
      return {
        label: 'Rooms',
        description: 'Select rooms and quantities for this project',
      };
  }
}
